// Standalone-web data shell. The web build aliases imports of `./data/useParcels` to THIS module, so
// the shared frontend gets its data from the /api/data Worker (REST proxy) instead of Airtable's live
// useRecords. It exposes the SAME public surface as useParcels.ts: the `useParcels()` hook (identical
// return contract) plus the re-exported types/helpers consumers pull from '../data/useParcels'.
//
// Flow: fetch('/api/data') → assembleParcels() runs the SAME pure joinCore builders the extension uses
// → parcels/dock identical to the extension. Edge-cached at the Worker; `refresh()` forces latest
// (?fresh=1); a light 3-min poll keeps it live. Empty/typed state before the first payload + on error.
import { useState, useEffect, useCallback, useRef } from 'react';
import { buildDockData, buildSyncedAt } from './joinCore';
import type { Parcel, DockData } from './joinCore';
import { assembleParcels, type ApiPayload, type AssembledData, type MissingField } from './assembleParcels';

// Mirror useParcels.ts's re-exports so `import { ... } from '../data/useParcels'` resolves either way.
export type {
  LotMeta, RowBase, PermitRow, InspectionRow, SalesRow, SalesHistoryEntry, UnitRow, PunchRow, LotUpdateRow, ChangeLogRow,
  Attachment, DrawingSet, ModelProjection, DockData, BuildPhase, SalesBucket,
  UnitProjection, Parcel, InspRow,
} from './joinCore';
export { salesBucketFromSummary } from './joinCore';
export type { MissingField } from './assembleParcels';

const API = '/api/data';
const POLL_MS = 180_000;

type Result = { parcels: Parcel[]; loading: boolean; fieldNames: Record<string, string>;
  missingFields: MissingField[]; refresh: () => void } & DockData;

function emptyState(loading: boolean): AssembledData & { loading: boolean } {
  return { parcels: [], loading, ...buildDockData({}), fieldNames: {}, missingFields: [], syncedAt: buildSyncedAt([], []) };
}

export function useParcels(): Result {
  const [state, setState] = useState<AssembledData & { loading: boolean }>(() => emptyState(true));
  const alive = useRef(true);

  const load = useCallback(async (fresh: boolean) => {
    try {
      const res = await fetch(API + (fresh ? '?fresh=1' : ''), { credentials: 'include' });
      if (!res.ok) throw new Error('api ' + res.status);
      const payload = (await res.json()) as ApiPayload;
      const data = assembleParcels(payload);
      if (alive.current) setState({ ...data, loading: false });
    } catch (e) {
      // Keep the last good data on a transient failure; just clear the loading flag.
      if (alive.current) setState((s) => ({ ...s, loading: false }));
      if (typeof console !== 'undefined') console.error('useParcelsApi load failed', e);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load(false);
    const id = setInterval(() => load(false), POLL_MS);
    return () => { alive.current = false; clearInterval(id); };
  }, [load]);

  const refresh = useCallback(() => { setState((s) => ({ ...s, loading: true })); load(true); }, [load]);

  return { ...state, refresh };
}
